import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Assessment, CheckBox, CheckBoxOutlineBlank, Clear, Refresh } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import FilteredListLayout from '../FilteredListLayout';
import LinkPagination from '../LinkPagination';
import ListActionButton from '../ListActionButton';
import CreateUserActivityReportDialog, {
  type UserActivityReportSummaryEntry,
} from '../reports/CreateUserActivityReportDialog';
import { useLanguage } from '../../hooks/useLanguage';
import { useListSearchParams } from '../../hooks/useListSearchParams';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { usePermissions } from '../../contexts/PermissionContext';
import { fetchUserActivities, fetchUserOptions, type UserOption } from '../../services/users';
import { formatDateTime } from '../../utils/date';

import type { UserActivityEventItem } from '../../types';


type ActivityDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';

interface ActivityDateRangeOption {
  value: ActivityDateRangePreset;
  label: string;
}

const ACTIVITY_DATE_RANGE_PRESETS: readonly ActivityDateRangePreset[] = ['today', 'yesterday', 'last_7_days', 'period'];
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';
const USER_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const USER_ACTIVITY_REPORT_MAX_SPAN_DAYS = 31;
const ACTIVITY_CATEGORY_OPTIONS = ['session', 'call', 'report', 'campaign', 'admin', 'integration', 'script', 'telephony'] as const;
const ACTIVITY_ACTION_OPTIONS = [
  'session_login',
  'session_logout',
  'session_revoked',
  'session_expired',
  'call_manual_created',
  'call_human_participation',
  'call_claimed',
  'call_transfer_started',
  'call_monitor_started',
  'call_monitor_stopped',
  'call_hold_started',
  'call_hold_stopped',
  'call_mute_started',
  'call_mute_stopped',
  'call_terminated_manual',
  'campaign_report_requested',
  'call_report_requested',
  'user_activity_report_requested',
  'report_downloaded',
  'campaign_created',
  'campaign_started',
  'campaign_paused',
  'campaign_resumed',
  'campaign_finished',
  'user_created',
  'user_updated',
  'user_deactivated',
  'user_groups_changed',
  'user_terminal_changed',
  'user_telephony_unavailable_started',
  'user_telephony_available_restored',
  'contact_z_manual_bind',
  'contact_z_binding_replaced',
  'contact_z_test_webhook',
  'contact_z_connection_created',
  'contact_z_connection_updated',
  'contact_z_connection_connect_requested',
  'contact_z_connection_disconnected',
  'contact_z_connection_restarted',
  'contact_z_connection_deleted',
  'script_created',
  'script_updated',
  'script_deleted',
  'script_manual_executed',
  'script_manual_executed_for_call',
] as const;
type ActivityActionValue = (typeof ACTIVITY_ACTION_OPTIONS)[number];
type ActivityCategoryValue = (typeof ACTIVITY_CATEGORY_OPTIONS)[number];
const ACTIVITY_ACTIONS_BY_CATEGORY: Record<ActivityCategoryValue, readonly ActivityActionValue[]> = {
  session: ['session_expired', 'session_login', 'session_logout', 'session_revoked'],
  call: [
    'call_claimed',
    'call_hold_started',
    'call_hold_stopped',
    'call_mute_started',
    'call_mute_stopped',
    'call_human_participation',
    'call_manual_created',
    'call_monitor_started',
    'call_monitor_stopped',
    'call_terminated_manual',
    'call_transfer_started',
  ],
  report: ['call_report_requested', 'campaign_report_requested', 'report_downloaded', 'user_activity_report_requested'],
  campaign: ['campaign_created', 'campaign_finished', 'campaign_paused', 'campaign_resumed', 'campaign_started'],
  admin: ['user_created', 'user_deactivated', 'user_groups_changed', 'user_terminal_changed', 'user_updated'],
  integration: [
    'contact_z_binding_replaced',
    'contact_z_connection_connect_requested',
    'contact_z_connection_created',
    'contact_z_connection_deleted',
    'contact_z_connection_disconnected',
    'contact_z_connection_restarted',
    'contact_z_connection_updated',
    'contact_z_manual_bind',
    'contact_z_test_webhook',
  ],
  script: ['script_created', 'script_deleted', 'script_manual_executed', 'script_manual_executed_for_call', 'script_updated'],
  telephony: ['user_telephony_available_restored', 'user_telephony_unavailable_started'],
};

interface ActivityActionOption {
  value: ActivityActionValue;
  category: ActivityCategoryValue;
  groupLabel: string;
  kind: 'action';
  label: string;
}

interface ActivityCategoryOption {
  value: ActivityCategoryValue;
  category: ActivityCategoryValue;
  groupLabel: string;
  kind: 'category';
  label: string;
}

type ActivityFilterOption = ActivityActionOption | ActivityCategoryOption;

function isActivityDateRangePreset(value: string | null | undefined): value is ActivityDateRangePreset {
  if (!value) {
    return false;
  }
  return ACTIVITY_DATE_RANGE_PRESETS.includes(value as ActivityDateRangePreset);
}

function buildFilterFieldSx(minWidth: number): SxProps<Theme> {
  return {
    minWidth,
    flex: `1 1 ${minWidth}px`,
  };
}

function stringifyMetadataValue(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean);
    return normalized.length > 0 ? normalized.join(', ') : null;
  }
  return null;
}

function parseCsvFilter(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  const seen = new Set<string>();
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => {
      if (!entry || seen.has(entry)) {
        return false;
      }
      seen.add(entry);
      return true;
    });
}

function normalizeReportType(value: string | null): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function parseDurationSeconds(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.floor(parsed));
    }
  }
  return null;
}

export default function UserActivitiesTab() {
  const { t } = useTranslation(['pages', 'common']);
  const { language } = useLanguage();
  const { formatDuration } = useNumberFormat();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableRowHoverBg = alpha(theme.palette.primary.main, 0.08);
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
    '& .MuiPickersInputBase-root': {
      padding: '3px 14px 2px 14px',
    },
  };

  const [events, setEvents] = useState<UserActivityEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalEvents, setTotalEvents] = useState(0);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [isCreateUserActivityReportOpen, setIsCreateUserActivityReportOpen] = useState(false);
  const userRequestIdRef = useRef(0);

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    pageParam: 'activity_page',
    pageSizeParam: 'activity_page_size',
    filters: ['activity_user', 'activity_category', 'activity_action', 'activity_date_range', 'activity_start_date', 'activity_end_date'],
    defaultPage: 1,
    defaultPageSize: 25,
    defaultOrdering: '-occurred_at',
  });

  const selectedUserId = filters.activity_user ?? '';
  const selectedCategoryValues = useMemo<ActivityCategoryValue[]>(
    () => parseCsvFilter(filters.activity_category).filter((value): value is ActivityCategoryValue => ACTIVITY_CATEGORY_OPTIONS.includes(value as ActivityCategoryValue)),
    [filters.activity_category],
  );
  const selectedActionValues = useMemo<ActivityActionValue[]>(
    () => parseCsvFilter(filters.activity_action).filter((value): value is ActivityActionValue => ACTIVITY_ACTION_OPTIONS.includes(value as ActivityActionValue)),
    [filters.activity_action],
  );
  const startDateFilterValue = filters.activity_start_date ?? '';
  const endDateFilterValue = filters.activity_end_date ?? '';
  const selectedDateRange = useMemo<ActivityDateRangePreset | null>(() => {
    if (isActivityDateRangePreset(filters.activity_date_range)) {
      return filters.activity_date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.activity_date_range, startDateFilterValue]);

  const dateRangeOptions = useMemo<ActivityDateRangeOption[]>(
    () => [
      { value: 'today', label: t('pages:calls.filters.today') },
      { value: 'yesterday', label: t('pages:calls.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:calls.filters.last7Days') },
      { value: 'period', label: t('pages:calls.filters.customPeriod') },
    ],
    [t],
  );
  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );

  const startDateValue = useMemo<Dayjs | null>(() => {
    if (!startDateFilterValue) {
      return null;
    }
    const value = dayjs(startDateFilterValue);
    return value.isValid() ? value : null;
  }, [startDateFilterValue]);
  const endDateValue = useMemo<Dayjs | null>(() => {
    if (!endDateFilterValue) {
      return null;
    }
    const value = dayjs(endDateFilterValue);
    return value.isValid() ? value : null;
  }, [endDateFilterValue]);

  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || undefined,
        endDate: endDateFilterValue || undefined,
      };
    }
    if (!selectedDateRange) {
      return { startDate: undefined, endDate: undefined };
    }

    const today = dayjs();
    if (selectedDateRange === 'today') {
      const value = today.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    if (selectedDateRange === 'yesterday') {
      const value = today.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    return {
      startDate: today.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT),
      endDate: today.format(DATE_FILTER_PARAM_FORMAT),
    };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);

  const selectedUserOption = useMemo(
    () => userOptions.find((option) => option.id === selectedUserId) ?? null,
    [selectedUserId, userOptions],
  );
  const eventFilterOptions = useMemo<ActivityFilterOption[]>(
    () =>
      ACTIVITY_CATEGORY_OPTIONS.flatMap((category) => {
        const groupLabel = t(`pages:users.activity.categories.${category}`, { defaultValue: category });
        const categoryOption: ActivityCategoryOption = {
          value: category,
          category,
          groupLabel,
          kind: 'category',
          label: groupLabel,
        };
        const actionOptions = [...ACTIVITY_ACTIONS_BY_CATEGORY[category]]
          .map<ActivityActionOption>((value) => ({
            value,
            category,
            groupLabel,
            kind: 'action',
            label: t(`pages:users.activity.actions.${value}`, { defaultValue: value }),
          }))
          .sort((left, right) => left.label.localeCompare(right.label, language));
        return [categoryOption, ...actionOptions];
      }),
    [language, t],
  );
  const selectedEventFilterOptions = useMemo(
    () =>
      eventFilterOptions.filter((option) =>
        option.kind === 'category'
          ? selectedCategoryValues.includes(option.value)
          : selectedActionValues.includes(option.value),
      ),
    [eventFilterOptions, selectedActionValues, selectedCategoryValues],
  );
  const selectedCategorySet = useMemo(() => new Set<ActivityCategoryValue>(selectedCategoryValues), [selectedCategoryValues]);
  const selectedCategoryLabels = useMemo(
    () => selectedCategoryValues.map((value) => t(`pages:users.activity.categories.${value}`, { defaultValue: value })),
    [selectedCategoryValues, t],
  );
  const selectedActionLabels = useMemo(
    () => selectedActionValues.map((value) => t(`pages:users.activity.actions.${value}`, { defaultValue: value })),
    [selectedActionValues, t],
  );
  const userActivityReportPeriodSummary = useMemo(() => {
    if (!resolvedDateParams.startDate || !resolvedDateParams.endDate) {
      return null;
    }
    const start = dayjs(resolvedDateParams.startDate);
    const end = dayjs(resolvedDateParams.endDate);
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    return `${start.locale(dayjsLocale).format('L')} → ${end.locale(dayjsLocale).format('L')}`;
  }, [dayjsLocale, resolvedDateParams.endDate, resolvedDateParams.startDate]);
  const userActivityReportValidationMessage = useMemo(() => {
    if (!selectedDateRange) {
      return t('pages:reports.users.create.periodRequired');
    }
    if (!resolvedDateParams.startDate || !resolvedDateParams.endDate) {
      return t('pages:reports.users.create.periodRequired');
    }
    const start = dayjs(resolvedDateParams.startDate);
    const end = dayjs(resolvedDateParams.endDate);
    if (!start.isValid() || !end.isValid()) {
      return t('pages:reports.users.create.periodRequired');
    }
    if (end.endOf('day').diff(start.startOf('day'), 'day', true) > USER_ACTIVITY_REPORT_MAX_SPAN_DAYS) {
      return t('pages:reports.users.create.periodTooLarge');
    }
    return null;
  }, [resolvedDateParams.endDate, resolvedDateParams.startDate, selectedDateRange, t]);
  const userActivityReportFiltersPayload = useMemo<Record<string, string>>(() => {
    const nextPayload: Record<string, string> = {
      ordering: ordering || '-occurred_at',
    };
    if (selectedUserId) {
      nextPayload.user = selectedUserId;
    }
    if (selectedCategoryValues.length > 0) {
      nextPayload.category = selectedCategoryValues.join(',');
    }
    if (selectedActionValues.length > 0) {
      nextPayload.action = selectedActionValues.join(',');
    }
    if (selectedDateRange) {
      nextPayload.date_range = selectedDateRange;
    }
    if (resolvedDateParams.startDate) {
      nextPayload.start_date = resolvedDateParams.startDate;
    }
    if (resolvedDateParams.endDate) {
      nextPayload.end_date = resolvedDateParams.endDate;
    }
    return nextPayload;
  }, [
    ordering,
    resolvedDateParams.endDate,
    resolvedDateParams.startDate,
    selectedActionValues,
    selectedCategoryValues,
    selectedDateRange,
    selectedUserId,
  ]);
  const userActivityReportSummaryEntries = useMemo<UserActivityReportSummaryEntry[]>(() => {
    const periodValue = userActivityReportPeriodSummary || t('pages:reports.users.create.periodMissingDisplay');
    return [
      {
        key: 'period',
        label: t('pages:calls.filters.dateRangeLabel'),
        values: [periodValue],
        warning: userActivityReportPeriodSummary ? null : t('pages:reports.users.create.periodRequired'),
      },
      {
        key: 'user',
        label: t('pages:users.activity.filters.userLabel'),
        values: selectedUserOption ? [selectedUserOption.label] : selectedUserId ? [selectedUserId] : [],
      },
      {
        key: 'category',
        label: t('pages:users.activity.filters.categoryLabel'),
        values: selectedCategoryLabels,
      },
      {
        key: 'action',
        label: t('pages:users.activity.filters.eventTypeLabel'),
        values: selectedActionLabels,
      },
    ];
  }, [
    selectedActionLabels,
    selectedCategoryLabels,
    selectedUserId,
    selectedUserOption,
    t,
    userActivityReportPeriodSummary,
  ]);
  const defaultUserActivityReportName = useMemo(() => {
    const parts = [t('pages:reports.users.create.defaultName')];
    if (selectedUserOption?.label) {
      parts.push(selectedUserOption.label);
    } else if (selectedUserId) {
      parts.push(selectedUserId);
    } else if (selectedCategoryLabels.length === 1) {
      parts.push(selectedCategoryLabels[0]);
    } else if (selectedActionLabels.length === 1) {
      parts.push(selectedActionLabels[0]);
    }
    if (userActivityReportPeriodSummary) {
      parts.push(userActivityReportPeriodSummary);
    }
    return parts.filter(Boolean).join(' • ');
  }, [selectedActionLabels, selectedCategoryLabels, selectedUserId, selectedUserOption, t, userActivityReportPeriodSummary]);
  const defaultUserActivityReportDescription = useMemo(() => {
    return userActivityReportSummaryEntries
      .filter((entry) => entry.values.length > 0)
      .map((entry) => `${entry.label}: ${entry.values.join(', ')}`)
      .join(' | ');
  }, [userActivityReportSummaryEntries]);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchUserActivities({
        page: page + 1,
        pageSize: rowsPerPage,
        ordering: ordering || '-occurred_at',
        user: selectedUserId || undefined,
        category: selectedCategoryValues.length > 0 ? selectedCategoryValues : undefined,
        action: selectedActionValues.length > 0 ? selectedActionValues : undefined,
        startDate: resolvedDateParams.startDate,
        endDate: resolvedDateParams.endDate,
      });
      setEvents(response.results);
      setTotalEvents(response.count);
    } catch (error) {
      console.error('Error loading user activities:', error);
      setEvents([]);
      setTotalEvents(0);
    } finally {
      setLoading(false);
    }
  }, [
    ordering,
    page,
    resolvedDateParams.endDate,
    resolvedDateParams.startDate,
    rowsPerPage,
    selectedActionValues,
    selectedCategoryValues,
    selectedUserId,
  ]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    const requestId = userRequestIdRef.current + 1;
    userRequestIdRef.current = requestId;
    setIsUserLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const nextOptions = await fetchUserOptions({
          search: userSearchInput || undefined,
          selectedId: selectedUserId || undefined,
          limit: 10,
        });
        if (userRequestIdRef.current === requestId) {
          setUserOptions(nextOptions);
        }
      } catch (error) {
        if (userRequestIdRef.current === requestId) {
          console.error('Error loading user options:', error);
          setUserOptions([]);
        }
      } finally {
        if (userRequestIdRef.current === requestId) {
          setIsUserLoading(false);
        }
      }
    }, USER_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedUserId, userSearchInput]);

  const renderActionLabel = useCallback((event: UserActivityEventItem) => {
    return t(`pages:users.activity.actions.${event.action}`, { defaultValue: event.action });
  }, [t]);

  const renderTelephonyReason = useCallback((value: unknown) => {
    const reason = stringifyMetadataValue(value);
    if (!reason) {
      return null;
    }
    return t(`common:operatorAvailability.states.${reason}`, { defaultValue: reason });
  }, [t]);

  const renderActivityDetail = useCallback((event: UserActivityEventItem) => {
    const metadata = event.metadata || {};

    if (event.action === 'call_transfer_started') {
      const destination = stringifyMetadataValue(metadata.destination_value);
      if (destination) {
        return t('pages:users.activity.details.destination', { value: destination });
      }
    }

    if (event.action === 'user_groups_changed') {
      const nextGroups = stringifyMetadataValue(metadata.next_group_names);
      if (nextGroups) {
        return t('pages:users.activity.details.groups', { value: nextGroups });
      }
    }

    if (event.action === 'user_terminal_changed') {
      const nextEndpoint = metadata.next_endpoint;
      if (typeof nextEndpoint === 'object' && nextEndpoint !== null) {
        const endpointType = stringifyMetadataValue((nextEndpoint as Record<string, unknown>).endpoint_type);
        const extension = stringifyMetadataValue((nextEndpoint as Record<string, unknown>).extension);
        if (extension) {
          return t('pages:users.activity.details.endpointExtension', { endpointType: endpointType || '-', extension });
        }
        if (endpointType) {
          return t('pages:users.activity.details.endpointType', { value: endpointType });
        }
      }
    }

    if (event.action === 'report_downloaded') {
      const reportType = normalizeReportType(stringifyMetadataValue(metadata.report_type));
      if (reportType) {
        const translatedReportType = t(`pages:users.activity.reportTypes.${reportType}`, {
          defaultValue: t(`pages:reports.generationLock.types.${reportType}`, { defaultValue: reportType }),
        });
        return t('pages:users.activity.details.reportType', { value: translatedReportType });
      }
    }

    if (event.action === 'call_monitor_started' || event.action === 'call_monitor_stopped') {
      const sessionId = stringifyMetadataValue(metadata.session_id);
      const durationSeconds = parseDurationSeconds(metadata.duration_seconds);
      const durationLabel = durationSeconds != null ? formatDuration(durationSeconds) : null;
      if (event.action === 'call_monitor_stopped' && sessionId && durationLabel) {
        return t('pages:users.activity.details.monitorSessionDuration', {
          sessionId,
          duration: durationLabel,
        });
      }
      if (sessionId) {
        return t('pages:users.activity.details.sessionId', { value: sessionId });
      }
      if (event.action === 'call_monitor_stopped' && durationLabel) {
        return t('pages:users.activity.details.telephonyDuration', { value: durationLabel });
      }
    }

    if (
      event.action === 'call_hold_stopped'
      || event.action === 'call_mute_stopped'
    ) {
      const durationSeconds = parseDurationSeconds(metadata.duration_seconds);
      const durationLabel = durationSeconds != null ? formatDuration(durationSeconds) : null;
      if (durationLabel) {
        return t('pages:users.activity.details.telephonyDuration', { value: durationLabel });
      }
    }

    if (event.action === 'user_telephony_unavailable_started') {
      const reasonLabel = renderTelephonyReason(metadata.reason);
      if (reasonLabel) {
        return t('pages:users.activity.details.telephonyReason', { value: reasonLabel });
      }
    }

    if (event.action === 'user_telephony_available_restored') {
      const reasonLabel = renderTelephonyReason(metadata.reason);
      const durationSeconds = parseDurationSeconds(metadata.duration_seconds);
      const durationLabel = durationSeconds != null ? formatDuration(durationSeconds) : null;

      if (reasonLabel && durationLabel) {
        return t('pages:users.activity.details.telephonyReasonDuration', {
          reason: reasonLabel,
          duration: durationLabel,
        });
      }
      if (reasonLabel) {
        return t('pages:users.activity.details.telephonyReason', { value: reasonLabel });
      }
      if (durationLabel) {
        return t('pages:users.activity.details.telephonyDuration', { value: durationLabel });
      }
    }

    return '';
  }, [formatDuration, renderTelephonyReason, t]);

  const renderResourceLabel = useCallback((event: UserActivityEventItem) => {
    if (event.resourceLabel) {
      return event.resourceLabel;
    }
    if (event.resourceType) {
      return t(`pages:users.activity.resourceTypes.${event.resourceType}`, { defaultValue: event.resourceType });
    }
    return t('pages:users.activity.none');
  }, [t]);

  const buildResourceLink = useCallback((event: UserActivityEventItem) => {
    const resourceId = event.resourcePublicId;
    if (!resourceId) {
      return null;
    }

    switch (event.resourceType) {
      case 'call':
        return hasPermission('calls_access') ? `/calls/${resourceId}` : null;
      case 'campaign':
        return hasPermission('campaigns_access') ? `/campaigns/${resourceId}` : null;
      case 'campaign_report':
        return hasPermission('campaigns_access') ? '/reports?tab=campaigns' : null;
      case 'call_report':
        return hasPermission('calls_reports_access') ? '/reports?tab=calls' : null;
      case 'user_activity_report':
        return hasPermission('users_read') ? '/reports?tab=users' : null;
      case 'user':
      case 'user_terminal':
        return hasPermission('users_access') ? `/users/${resourceId}` : null;
      case 'script':
        return hasPermission('scripts_access') ? `/scripts/${resourceId}` : null;
      case 'script_execution': {
        const scriptId = stringifyMetadataValue(event.metadata?.script_id);
        return scriptId && hasPermission('scripts_access') ? `/scripts/${scriptId}` : null;
      }
      case 'contact_z_connection':
      case 'contact_z_integration':
        return hasPermission('contact_z_access') ? '/integrations/contact-z' : null;
      default:
        return null;
    }
  }, [hasPermission]);

  const handleUserActivityReportCreated = useCallback(() => {
    setIsCreateUserActivityReportOpen(false);
    navigate('/reports?tab=users');
  }, [navigate]);

  const handleSortToggle = useCallback(() => {
    const nextOrdering = ordering === 'occurred_at' ? '-occurred_at' : 'occurred_at';
    setParams({ ordering: nextOrdering }, { resetPage: true });
  }, [ordering, setParams]);

  const filterControls = (
    <>
      <Autocomplete<UserOption, false, false, false>
        options={userOptions}
        value={selectedUserOption}
        loading={isUserLoading}
        onChange={(_event, option) => {
          setParams({ activity_user: option?.id ?? null }, { resetPage: true });
        }}
        onInputChange={(_event, value, reason) => {
          if (reason === 'reset' && selectedUserOption) {
            setUserSearchInput(selectedUserOption.label);
            return;
          }
          setUserSearchInput(value);
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:users.activity.filters.userLabel')}
            sx={{ ...filterInputSx, minWidth: 260, flex: '1 1 260px' }}
          />
        )}
      />
      <Autocomplete<ActivityFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={eventFilterOptions}
        value={selectedEventFilterOptions}
        getOptionDisabled={(option) => option.kind === 'action' && selectedCategorySet.has(option.category)}
        onChange={(_event, options) => {
          const nextCategoryValues = options
            .filter((option): option is ActivityCategoryOption => option.kind === 'category')
            .map((option) => option.value);
          const nextCategorySet = new Set<ActivityCategoryValue>(nextCategoryValues);
          const nextActionValues = options
            .filter(
              (option): option is ActivityActionOption =>
                option.kind === 'action' && !nextCategorySet.has(option.category),
            )
            .map((option) => option.value);
          setParams(
            {
              activity_category: nextCategoryValues.length > 0 ? nextCategoryValues.join(',') : null,
              activity_action: nextActionValues.length > 0 ? nextActionValues.join(',') : null,
            },
            { resetPage: true },
          );
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        groupBy={(option) => option.groupLabel}
        renderOption={(props, option, { selected }) => {
          const categorySelected = option.kind === 'action' && selectedCategorySet.has(option.category);
          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                opacity: categorySelected ? 0.6 : 1,
              }}
            >
              <Checkbox
                checked={selected || categorySelected}
                disabled={categorySelected}
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBox fontSize="small" />}
                sx={{ p: 0.25 }}
              />
              <Typography fontWeight={option.kind === 'category' ? 700 : 400}>
                {option.kind === 'category'
                  ? t('pages:users.activity.filters.eventTypeGroupAll', { category: option.label })
                  : option.label}
              </Typography>
            </Box>
          );
        }}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 2,
                pt: 1.5,
                pb: 0.5,
                color: 'text.secondary',
                letterSpacing: '0.06em',
              }}
            >
              {params.group}
            </Typography>
            {params.children}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:users.activity.filters.eventTypeLabel')}
            sx={{ ...filterInputSx, ...buildFilterFieldSx(320) }}
          />
        )}
      />
      <Autocomplete<ActivityDateRangeOption, false, false, false>
        options={dateRangeOptions}
        value={dateRangeOption}
        onChange={(_event, option) => {
          const value = option?.value ?? null;
          if (!value) {
            setParams(
              {
                activity_date_range: null,
                activity_start_date: null,
                activity_end_date: null,
              },
              { resetPage: true },
            );
            return;
          }
          if (value === 'period') {
            setParams({ activity_date_range: value }, { resetPage: true });
            return;
          }
          setParams(
            {
              activity_date_range: value,
              activity_start_date: null,
              activity_end_date: null,
            },
            { resetPage: true },
          );
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.dateRangeLabel')}
            sx={{ ...filterInputSx, ...buildFilterFieldSx(220) }}
          />
        )}
      />
      {selectedDateRange === 'period' ? (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <DateTimePicker
            label={t('pages:calls.filters.from')}
            value={startDateValue}
            onChange={(value) => {
              setParams(
                {
                  activity_date_range: 'period',
                  activity_start_date: value?.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null,
                },
                { resetPage: true },
              );
            }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { ...filterInputSx, ...buildFilterFieldSx(220) },
              },
            }}
          />
          <DateTimePicker
            label={t('pages:calls.filters.until')}
            value={endDateValue}
            onChange={(value) => {
              setParams(
                {
                  activity_date_range: 'period',
                  activity_end_date: value?.isValid() ? value.format(DATE_FILTER_PARAM_FORMAT) : null,
                },
                { resetPage: true },
              );
            }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { ...filterInputSx, ...buildFilterFieldSx(220) },
              },
            }}
          />
        </LocalizationProvider>
      ) : null}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <ListActionButton
          variant="outlined"
          startIcon={<Assessment />}
          onClick={() => setIsCreateUserActivityReportOpen(true)}
        >
          {t('pages:reports.users.create.openButton')}
        </ListActionButton>
        <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={fetchActivity}>
          {t('common:actions.refresh')}
        </ListActionButton>
        <ListActionButton
          variant="outlined"
          startIcon={<Clear />}
          disabled={
            !selectedUserId
            && selectedCategoryValues.length === 0
            && selectedActionValues.length === 0
            && !selectedDateRange
            && !startDateFilterValue
            && !endDateFilterValue
          }
          onClick={() => {
            setParams(
              {
                activity_user: null,
                activity_category: null,
                activity_action: null,
                activity_date_range: null,
                activity_start_date: null,
                activity_end_date: null,
              },
              { resetPage: true },
            );
          }}
        >
          {t('common:actions.clear')}
        </ListActionButton>
      </Box>
    </>
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            },
          }}
        >
          <TableCell>
            <TableSortLabel
              active={ordering === 'occurred_at' || ordering === '-occurred_at'}
              direction={ordering === 'occurred_at' ? 'asc' : 'desc'}
              onClick={handleSortToggle}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('pages:users.activity.columns.occurredAt').toLocaleUpperCase(language)}
            </TableSortLabel>
          </TableCell>
          <TableCell>{t('pages:users.activity.columns.activity').toLocaleUpperCase(language)}</TableCell>
          <TableCell>{t('pages:users.activity.columns.actor').toLocaleUpperCase(language)}</TableCell>
          <TableCell>{t('pages:users.activity.columns.subject').toLocaleUpperCase(language)}</TableCell>
          <TableCell>{t('pages:users.activity.columns.resource').toLocaleUpperCase(language)}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(5)].map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {[...Array(5)].map((_, cellIndex) => (
                <TableCell key={cellIndex}><Skeleton /></TableCell>
              ))}
            </TableRow>
          ))
        ) : events.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} sx={{ py: 8 }}>
              <Typography color="text.secondary" textAlign="center">
                {t('pages:users.activity.empty')}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          events.map((event) => {
            const detailText = renderActivityDetail(event);
            const actorLabel = event.actorUserName || t('pages:users.activity.none');
            const subjectLabel = event.subjectUserName && event.subjectUserName !== actorLabel
              ? event.subjectUserName
              : t('pages:users.activity.none');
            const resourceLink = buildResourceLink(event);
            return (
              <TableRow
                key={event.id}
                hover
                sx={{
                  '& td': { verticalAlign: 'middle' },
                  '&:hover': { backgroundColor: tableRowHoverBg },
                }}
              >
                <TableCell>
                  <Typography fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(event.occurredAt, { locale: language })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{renderActionLabel(event)}</Typography>
                  {detailText ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {detailText}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography>{actorLabel}</Typography>
                  {event.actorSessionId ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:users.activity.details.sessionId', { value: event.actorSessionId })}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography>{subjectLabel}</Typography>
                </TableCell>
                <TableCell>
                  {resourceLink ? (
                    <Link
                      component={RouterLink}
                      to={resourceLink}
                      underline="hover"
                      color="inherit"
                      sx={{ display: 'block', width: 'fit-content', fontWeight: 500 }}
                    >
                      {renderResourceLabel(event)}
                    </Link>
                  ) : (
                    <Typography>{renderResourceLabel(event)}</Typography>
                  )}
                  {event.resourceType ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {t(`pages:users.activity.resourceTypes.${event.resourceType}`, { defaultValue: event.resourceType })}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const tableFooter = totalEvents > 0 ? (
    <LinkPagination
      component="div"
      count={totalEvents}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
      }}
      rowsPerPageOptions={[25, 50, 100]}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? `${from}-${to} ${t('common:common.of')} ${count}`
          : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
      }
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
    />
  ) : undefined;

  return (
    <>
      <FilteredListLayout
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
      />
      <CreateUserActivityReportDialog
        open={isCreateUserActivityReportOpen}
        onClose={() => setIsCreateUserActivityReportOpen(false)}
        filtersPayload={userActivityReportFiltersPayload}
        filterEntries={userActivityReportSummaryEntries}
        defaultName={defaultUserActivityReportName}
        defaultDescription={defaultUserActivityReportDescription}
        canConfirm={!userActivityReportValidationMessage}
        validationMessage={userActivityReportValidationMessage}
        onCreated={handleUserActivityReportCreated}
      />
    </>
  );
}
